import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between h-16">
        <a href="/" className="text-foreground-900 font-heading text-xl font-bold tracking-tight whitespace-nowrap cursor-pointer">
          CSSSpreadsheet<span className="text-primary-500">.</span>fun
        </a>

        <div className="hidden md:flex items-center gap-6">
          <a
            href="/"
            className="flex items-center gap-1.5 text-foreground-700 hover:text-foreground-900 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line text-base"></i>
            Home
          </a>

          <a
            href="/cssbuy-spreadsheet-guide-2026"
            className="flex items-center gap-1.5 text-foreground-700 hover:text-foreground-900 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-book-open-line text-base"></i>
            Shopping Guide
          </a>

          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-700 hover:text-foreground-900 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer">
            CSSBuy Sheet
          </a>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span>Categories</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-80 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                <div className="max-h-96 overflow-y-auto py-2">
                  {categories.map((cat, idx) => (
                    <a
                      key={idx}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-foreground-800 text-sm font-medium block truncate">{cat.name}</span>
                        <span className="text-foreground-400 text-[11px]">{cat.count}</span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-800"
        >
          <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-[fadeIn_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <div className="flex items-center gap-2 mb-2">
              <a
                href="/"
                className="flex items-center gap-2 px-3 py-3 text-foreground-800 text-sm font-semibold hover:bg-background-100 rounded-xl transition-colors cursor-pointer flex-1 justify-center"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-home-4-line text-lg"></i>
                Home
              </a>
              <a
                href="/cssbuy-spreadsheet-guide-2026"
                className="flex items-center gap-2 px-3 py-3 text-foreground-800 text-sm font-semibold hover:bg-background-100 rounded-xl transition-colors cursor-pointer flex-1 justify-center"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-book-open-line text-lg"></i>
                Shopping Guide
              </a>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-4 py-3 text-foreground-800 text-sm font-medium bg-background-100 hover:bg-background-200 rounded-xl transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-external-link-line"></i>
              CSSBuy Sheet
            </a>

            <div className="border-t border-background-200/70 my-3"></div>
            <p className="px-2 py-1 text-foreground-400 text-[11px] tracking-[0.15em] uppercase font-semibold">Categories</p>

            {categories.map((cat, idx) => (
              <a
                key={idx}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-2 py-2.5 text-foreground-800 text-sm hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{cat.name}</span>
                  <span className="text-foreground-400 text-[11px]">{cat.count}</span>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </a>
            ))}

            <div className="border-t border-background-200/70 my-3"></div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all cursor-pointer mt-1"
              onClick={() => setMobileOpen(false)}
            >
              Buy It Now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}